"use client";
import { useState, useCallback, useMemo, useEffect, useRef } from "react";
import useToastNotification from "@/hook/useToastNotification";
import { PhoneInput } from "react-international-phone";
import { PHONE_REGEX } from "@/utils/Constants";
import { useAppDispatch } from "@/store/hooks";
import {
	getUserDetail,
	sentOtp,
	updatePrimaryPhone,
	verifyOtp,
} from "@/store/feature/user/userApi";
import { useAuth } from "@/hook/useAuth";
import {
	InputOTP,
	InputOTPGroup,
	InputOTPSeparator,
	InputOTPSlot,
} from "../ui/input-otp";
import { REGEXP_ONLY_DIGITS } from "input-otp";

interface PhoneVerificationProps {
	setPhoneVerificationPopup: React.Dispatch<React.SetStateAction<boolean>>;
}

export default function PhoneVerification({
	setPhoneVerificationPopup,
}: PhoneVerificationProps) {
	const dispatch = useAppDispatch();
	const { user } = useAuth();
	const { showToast } = useToastNotification();
	const timerRef = useRef<NodeJS.Timeout | null>(null);

	// State management
	const [state, setState] = useState({
		loading: false,
		otpSent: false,
		phoneNumber: "",
		verifiedPhoneNumber: "",
		otp: "",
		otpLoading: false,
		resendLoading: false,
		resendCountdown: 0, // New countdown state
	});

	// Cleanup timer on unmount
	useEffect(() => {
		return () => {
			if (timerRef.current) {
				clearInterval(timerRef.current);
			}
		};
	}, []);

	// Start countdown timer
	const startCountdown = useCallback(() => {
		setState((prev) => ({ ...prev, resendCountdown: 60 }));

		timerRef.current = setInterval(() => {
			setState((prev) => {
				if (prev.resendCountdown <= 1) {
					if (timerRef.current) {
						clearInterval(timerRef.current);
						timerRef.current = null;
					}
					return { ...prev, resendCountdown: 0 };
				}
				return { ...prev, resendCountdown: prev.resendCountdown - 1 };
			});
		}, 1000);
	}, []);

	// Memoized computed values
	const hasPhoneNumberChanged = useMemo(
		() => state.otpSent && state.phoneNumber !== state.verifiedPhoneNumber,
		[state.otpSent, state.phoneNumber, state.verifiedPhoneNumber],
	);

	const showOtpButton = useMemo(
		() => !state.otpSent || hasPhoneNumberChanged,
		[state.otpSent, hasPhoneNumberChanged],
	);

	const isPhoneValid = useMemo(() => {
		if (!state.phoneNumber.trim()) return false;
		return (
			!process.env.NEXT_PUBLIC_PRODUCTION_HOST ||
			PHONE_REGEX.test(state.phoneNumber)
		);
	}, [state.phoneNumber]);

	const canResendOtp = useMemo(
		() =>
			state.resendCountdown === 0 && !state.resendLoading && !state.otpLoading,
		[state.resendCountdown, state.resendLoading, state.otpLoading],
	);

	// Event handlers with proper error handling
	const handleBackdropClick = useCallback(
		(e: React.MouseEvent) => {
			// Only close if clicking the backdrop itself
			if (e.target === e.currentTarget) {
				setPhoneVerificationPopup(false);
			}
		},
		[setPhoneVerificationPopup],
	);

	const handlePhoneChange = useCallback((value: string) => {
		setState((prev) => ({
			...prev,
			phoneNumber: value,
			// Clear OTP if changing to a different number after OTP was sent
			...(prev.otpSent && value !== prev.verifiedPhoneNumber && { otp: "" }),
		}));
	}, []);

	// Handle OTP input change - only allow numbers
	const handleOtpChange = useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => {
			const value = e.target.value;
			// Only allow numeric input
			const numericValue = value.replace(/\D/g, "");
			setState((prev) => ({ ...prev, otp: numericValue }));
		},
		[],
	);

	const sendOTP = useCallback(async () => {
		if (!isPhoneValid) {
			showToast(
				state.phoneNumber.trim()
					? "Invalid phone number"
					: "Please enter a valid phone number.",
				"error",
			);
			return;
		}

		try {
			setState((prev) => ({ ...prev, loading: true }));

			await dispatch(sentOtp({ phoneNumber: state.phoneNumber })).unwrap();

			showToast("OTP sent successfully! Please check your phone.", "success");
			setState((prev) => ({
				...prev,
				otpSent: true,
				verifiedPhoneNumber: state.phoneNumber,
				otp: "",
			}));

			startCountdown();
		} catch (error) {
			if (typeof error === "object" && error !== null && "message" in error) {
				showToast(`Failed to send OTP. ${String(error.message)}`, "error");
			} else {
				showToast("Failed to send OTP. Something went wrong", "error");
			}
		} finally {
			setState((prev) => ({ ...prev, loading: false }));
		}
	}, [dispatch, isPhoneValid, state.phoneNumber, showToast, startCountdown]);

	const resendOTP = useCallback(async () => {
		if (!state.verifiedPhoneNumber.trim()) {
			showToast("Please enter a valid phone number.", "error");
			return;
		}

		if (!canResendOtp) {
			return;
		}

		try {
			setState((prev) => ({ ...prev, resendLoading: true }));

			await dispatch(
				sentOtp({ phoneNumber: state.verifiedPhoneNumber }),
			).unwrap();

			showToast("OTP resent successfully! Please check your phone.", "success");
			setState((prev) => ({ ...prev, otp: "" }));

			// Start countdown timer again
			startCountdown();
		} catch (error) {
			console.error("Error resending OTP:", error);
			showToast("Failed to resend OTP. Please try again later.", "error");
		} finally {
			setState((prev) => ({ ...prev, resendLoading: false }));
		}
	}, [
		dispatch,
		state.verifiedPhoneNumber,
		showToast,
		canResendOtp,
		startCountdown,
	]);

	const verifyOTP = useCallback(async () => {
		if (!state.otp.trim() || state.otp.length !== 6) {
			showToast("Please enter a valid 6-digit OTP.", "error");
			return;
		}

		try {
			setState((prev) => ({ ...prev, otpLoading: true }));

			await dispatch(
				verifyOtp({
					phoneNumber: state.phoneNumber,
					otp: state.otp,
				}),
			).unwrap();

			showToast("Phone number verified successfully!", "success");

			if (user) {
				await dispatch(
					updatePrimaryPhone({
						phoneNumber: state.phoneNumber,
						userId: user.uid,
					}),
				).unwrap();
				dispatch(getUserDetail(user.uid || ""));
			}

			setPhoneVerificationPopup(false);
		} catch (error: any) {
			console.error("Error verifying OTP:", error.response.data.message);
			showToast(error.response.data.message, "error");
		} finally {
			setState((prev) => ({ ...prev, otpLoading: false }));
		}
	}, [
		dispatch,
		state.phoneNumber,
		state.otp,
		user,
		setPhoneVerificationPopup,
		showToast,
	]);

	// Button text logic
	const getOtpButtonText = () => {
		if (state.loading) return "Sending...";
		if (hasPhoneNumberChanged) return "Send OTP to New Number";
		if (state.otpSent) return "Send OTP Again";
		return "Send OTP";
	};

	// Get resend button text with countdown
	const getResendButtonText = () => {
		if (state.resendLoading) return "Resending...";
		if (state.resendCountdown > 0)
			return `Resend OTP (${state.resendCountdown}s)`;
		return "Resend OTP";
	};

	return (
		<div
			className="fixed bottom-0 left-0 w-full h-full z-[999] overflow-hidden flex items-end justify-center"
			onClick={handleBackdropClick}>
			<span className="inset-0 bg-black/60 backdrop-blur-[6px] absolute top-0 left-0 w-full h-full cursor-pointer" />
			<div
				className="ff_poppins bg-[#0E1116] text-white w-full max-w-md p-6 sm:p-10 rounded-t-[32px] relative z-60 flex flex-col items-center justify-center overflow-hidden shadow-[0px_-4px_12px_0px_#ffffff40] max-h-[90vh] overflow-y-auto"
				onClick={(e) => e.stopPropagation()}>
				<div className="z-100 flex flex-col items-center justify-center text-center w-full">
					<h4 className="mb-3 text-2xl font-semibold sm:text-lg">
						Verify Your Phone Number
					</h4>

					<p className="mb-6 text-base sm:text-lg font-normal text-center text-white/70">
						Please enter your phone number to receive a verification code.
					</p>

					{/* Phone Input Section */}
					<div className="w-full max-w-sm mb-4">
						<PhoneInput
							inputClassName="!text-[20px] !h-[50px]"
							className="w-full border-black border-[1.5px] rounded-[8px] !text-[14px] personal_details_dashboard"
							defaultCountry="in"
							forceDialCode
							hideDropdown={true}
							value={state.phoneNumber}
							placeholder="Enter your phone number"
							onChange={handlePhoneChange}
							disabled={state.loading || state.resendLoading}
							inputProps={{
								onPaste: (e) => {
									e.preventDefault();
								},
								onCopy: (e) => e.preventDefault(),
								onCut: (e) => e.preventDefault(),
							}}
						/>
					</div>

					{showOtpButton && (
						<button
							onClick={sendOTP}
							disabled={
								state.loading ||
								state.resendLoading ||
								!state.phoneNumber.trim()
							}
							className="w-full max-w-sm px-6 py-3 bg-button-gradient text-white rounded-[8px] disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors mb-6">
							{getOtpButtonText()}
						</button>
					)}

					{/* OTP Input Section */}
					{state.otpSent && !hasPhoneNumberChanged && (
						<div className="w-full">
							<div className="pb-4 pt-4 flex justify-center">
								{/* <input
                  type="text"
                  value={state.otp}
                  onChange={handleOtpChange}
                  maxLength={6}
                  placeholder="Enter 6-digit OTP"
                  inputMode="numeric"
                  pattern="[0-9]*"
									className={`w-full border rounded-[8px] bg-white placeholder-black text-[#404040] leading-[21px] ff_poppins font-normal text-sm py-[7.5px] px-4 transition-all duration-200 ease-in-out ${
                    state.otp.length === 6
                      ? "border-black"
                      : "border-[rgba(0,0,0,0.37)]"
                  }`}
                /> */}
								<InputOTP
									required
									pattern={REGEXP_ONLY_DIGITS}
									maxLength={6}
									value={state.otp}
									onChange={(val) =>
										setState((prev) => ({ ...prev, otp: val }))
									}>
									<InputOTPGroup>
										<InputOTPSlot
											index={0}
											className="ff_poppins text-xl sm:!text-[20px] h-[40px] lg:!h-[45px] xl:!h-[50px] !w-[35px] lg:!w-[40px] xl:!w-[40px]"
										/>
										<InputOTPSlot
											index={1}
											className="ff_poppins text-xl sm:!text-[20px] h-[40px] lg:!h-[45px] xl:!h-[50px] !w-[35px] lg:!w-[40px] xl:!w-[40px]"
										/>
										<InputOTPSlot
											index={2}
											className="ff_poppins text-xl sm:!text-[20px] h-[40px] lg:!h-[45px] xl:!h-[50px] !w-[35px] lg:!w-[40px] xl:!w-[40px]"
										/>
									</InputOTPGroup>
									<InputOTPSeparator />
									<InputOTPGroup>
										<InputOTPSlot
											index={3}
											className="ff_poppins text-xl sm:!text-[20px] h-[40px] lg:!h-[45px] xl:!h-[50px] !w-[35px] lg:!w-[40px] xl:!w-[40px]"
										/>
										<InputOTPSlot
											index={4}
											className="ff_poppins text-xl sm:!text-[20px] h-[40px] lg:!h-[45px] xl:!h-[50px] !w-[35px] lg:!w-[40px] xl:!w-[40px]"
										/>
										<InputOTPSlot
											index={5}
											className="ff_poppins text-xl sm:!text-[20px] h-[40px] lg:!h-[45px] xl:!h-[50px] !w-[35px] lg:!w-[40px] xl:!w-[40px]"
										/>
									</InputOTPGroup>
								</InputOTP>
							</div>

							{/* Resend OTP Button with Countdown */}
							<div className="pb-6 flex justify-center">
								<button
									onClick={resendOTP}
									disabled={!canResendOtp}
									className={`text-sm font-medium underline transition-colors ${
										canResendOtp
											? "text-blue-600 hover:text-blue-800 cursor-pointer"
											: "text-gray-400 no-underline cursor-not-allowed"
									}`}>
									{getResendButtonText()}
								</button>
							</div>

							<button
								onClick={verifyOTP}
								disabled={state.otpLoading}
								className="w-full max-w-sm px-6 py-3 bg-button-gradient text-white rounded-[8px] disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors mb-6">
								{state.otpLoading ? "Verifying..." : "Verify Now"}
							</button>
						</div>
					)}
				</div>
			</div>
		</div>
	);
}
